"use client";

import { useEffect, useRef } from "react";
import {
  SLIDE_W, SLIDE_H,
  type CoverSlideData, type SpeciesSlideData, type ClosingSlideData, type CarouselTemplate, type SlideStyle,
} from "@/lib/carousel/types";
import FieldDiaryCoverOverlay from "./FieldDiaryCoverOverlay";
import FieldDiarySpeciesOverlay from "./FieldDiarySpeciesOverlay";
import FieldDiaryClosingOverlay from "./FieldDiaryClosingOverlay";
import MinimalLightSpeciesOverlay from "./MinimalLightSpeciesOverlay";
import MinimalLightClosingOverlay from "./MinimalLightClosingOverlay";

const SLOT = 400; // card slot width in px — shared by track centering and per-card placement

export interface StageCardInput {
  key: string;
  kind: "cover" | "species" | "closing";
  data: CoverSlideData | SpeciesSlideData | ClosingSlideData;
  photoUrl: string | null;
}

export interface EditorStageProps {
  cards: StageCardInput[];
  activeIndex: number;
  setActiveIndex: (i: number) => void;
  template: CarouselTemplate;
  style: SlideStyle;
  /** Opens the file picker to add/change the active card's photo. */
  onChangePhotoClick: () => void;
  /** Present only when the active card can be deleted (custom species slide, closing slide). */
  onRemoveActive?: () => void;
  /** Patches the active card's cover fields — wired to the text overlay when present. */
  onUpdateActiveCover?: (patch: Partial<{ kickerText: string; headlineTitle: string; subtitle: string }>) => void;
  /** Patches the active card's species name fields — wired to the text overlay when present. */
  onUpdateActiveSpecies?: (patch: Partial<{ namePrimary: string; nameSecondary: string; nameTertiary: string }>) => void;
  /** Patches the active card's closing fields — wired to the text overlay when present. */
  onUpdateActiveClosing?: (patch: Partial<{ heading: string; subtext: string; handle: string }>) => void;
}

function StageCard({
  card, template, style, index, distance, onChangePhotoClick, onNavigate, onRemoveActive,
  onUpdateActiveCover, onUpdateActiveSpecies, onUpdateActiveClosing,
}: {
  card: StageCardInput;
  template: CarouselTemplate;
  style: SlideStyle;
  index: number; // true position in the full card list — drives absolute placement
  distance: number; // 0 = active, ±1 = neighbor
  onChangePhotoClick: () => void;
  /** Jump the stage to this card — only wired on non-active (peeking) cards. */
  onNavigate: () => void;
  onRemoveActive?: () => void;
  onUpdateActiveCover?: (patch: Partial<{ kickerText: string; headlineTitle: string; subtitle: string }>) => void;
  onUpdateActiveSpecies?: (patch: Partial<{ namePrimary: string; nameSecondary: string; nameTertiary: string }>) => void;
  onUpdateActiveClosing?: (patch: Partial<{ heading: string; subtext: string; handle: string }>) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isActive = distance === 0;
  const useTextOverlay = isActive && !!template.useTextOverlay?.[card.kind];

  useEffect(() => {
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    const opts = useTextOverlay ? { skipEditableText: true } : undefined;
    if (card.kind === "cover") template.drawCover(ctx, card.data as CoverSlideData, style, opts);
    else if (card.kind === "species") template.drawSpecies(ctx, card.data as SpeciesSlideData, style, opts);
    else template.drawClosing(ctx, card.data as ClosingSlideData, style, opts);
    // card.data is a fresh object each render by design (merges the latest photo);
    // redrawing is cheap and only ≤3 cards are ever mounted at once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card.data, card.kind, template, style, useTextOverlay]);

  return (
    <div
      className={`ced-stage-slot ced-stage-slot--d${Math.abs(distance)}`}
      style={{ transform: `translateX(${index * SLOT}px)` }}
    >
      <div className="ced-stage-card">
        <canvas ref={canvasRef} width={SLIDE_W} height={SLIDE_H} className="ced-stage-canvas" />
        {isActive ? (
          <>
            {/* When a text overlay covers this card, the full-bleed photo
                hit-zone would sit under real text inputs — swap it for a
                small centered icon instead, so photo-upload stays reachable
                by a real click without competing with the text. */}
            {!useTextOverlay ? (
              <button
                type="button"
                className="ced-stage-hit ced-stage-hit--photo"
                onClick={onChangePhotoClick}
                aria-label={card.photoUrl ? "Change photo" : "Add photo"}
              />
            ) : (
              <button
                type="button"
                className="ced-stage-photo-icon"
                onClick={onChangePhotoClick}
                aria-label={card.photoUrl ? "Change photo" : "Add photo"}
                title={card.photoUrl ? "Change photo" : "Add photo"}
              >
                📷
              </button>
            )}
            {useTextOverlay && card.kind === "cover" && onUpdateActiveCover && (
              <FieldDiaryCoverOverlay
                data={card.data as CoverSlideData}
                fonts={style.fonts}
                update={onUpdateActiveCover}
              />
            )}
            {useTextOverlay && card.kind === "species" && onUpdateActiveSpecies && (
              template.id === "minimal-light" ? (
                <MinimalLightSpeciesOverlay
                  data={card.data as SpeciesSlideData}
                  fonts={style.fonts}
                  update={onUpdateActiveSpecies}
                />
              ) : (
                <FieldDiarySpeciesOverlay
                  data={card.data as SpeciesSlideData}
                  fonts={style.fonts}
                  update={onUpdateActiveSpecies}
                />
              )
            )}
            {useTextOverlay && card.kind === "closing" && onUpdateActiveClosing && (
              template.id === "minimal-light" ? (
                <MinimalLightClosingOverlay
                  data={card.data as ClosingSlideData}
                  fonts={style.fonts}
                  update={onUpdateActiveClosing}
                />
              ) : (
                <FieldDiaryClosingOverlay
                  data={card.data as ClosingSlideData}
                  fonts={style.fonts}
                  update={onUpdateActiveClosing}
                />
              )
            )}
            {onRemoveActive && (
              <button
                type="button"
                className="ced-stage-remove"
                onClick={(e) => { e.stopPropagation(); onRemoveActive(); }}
                aria-label="Remove slide"
                title="Remove slide"
              >
                🗑
              </button>
            )}
          </>
        ) : (
          <button
            type="button"
            className="ced-stage-hit ced-stage-hit--neighbor"
            onClick={onNavigate}
            aria-label="Jump to this slide"
          />
        )}
      </div>
    </div>
  );
}

export default function EditorStage({
  cards, activeIndex, setActiveIndex, template, style, onChangePhotoClick, onRemoveActive,
  onUpdateActiveCover, onUpdateActiveSpecies, onUpdateActiveClosing,
}: EditorStageProps) {
  const idx = Math.min(activeIndex, cards.length - 1);
  const goto = (i: number) => setActiveIndex(Math.max(0, Math.min(cards.length - 1, i)));

  const visible = cards
    .map((card, i) => ({ card, i, distance: i - idx }))
    .filter(({ distance }) => Math.abs(distance) <= 1);

  // Pure-pixel translateX — see the .ced-stage-track CSS comment for why
  // this avoids translateX(calc(50% - Npx)).
  const trackTransform = `translateX(${-idx * SLOT}px)`;

  return (
    <div className="ced-stage-scene">
      <span className="ced-stage-idx">{idx + 1} / {cards.length}</span>
      <button type="button" className="ced-stage-arrow ced-stage-arrow--left" disabled={idx === 0} onClick={() => goto(idx - 1)} aria-label="Previous slide">‹</button>
      <button type="button" className="ced-stage-arrow ced-stage-arrow--right" disabled={idx === cards.length - 1} onClick={() => goto(idx + 1)} aria-label="Next slide">›</button>
      <div className="ced-stage-track" style={{ transform: trackTransform }}>
        {visible.map(({ card, i, distance }) => (
          <StageCard
            key={card.key}
            card={card}
            template={template}
            style={style}
            index={i}
            distance={distance}
            onChangePhotoClick={onChangePhotoClick}
            onNavigate={() => goto(i)}
            onRemoveActive={i === idx ? onRemoveActive : undefined}
            onUpdateActiveCover={i === idx ? onUpdateActiveCover : undefined}
            onUpdateActiveSpecies={i === idx ? onUpdateActiveSpecies : undefined}
            onUpdateActiveClosing={i === idx ? onUpdateActiveClosing : undefined}
          />
        ))}
      </div>
    </div>
  );
}
